import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import * as log from "@/lib/logger";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sunrise, CheckCircle2, AlertTriangle, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Daily stand-up view.
 *
 * Left panel: personal composer — Yesterday / Today / Blockers. If the user
 * already posted today, the form loads their entry and re-submitting updates.
 * Right panel: team feed of today's stand-ups, with a "not posted yet" bucket
 * so managers can nudge stragglers.
 */
export default function StandupView({ subscribe }) {
  const { user } = useAuth();
  const [yesterday, setYesterday] = useState("");
  const [today, setToday] = useState("");
  const [blockers, setBlockers] = useState("");
  const [saving, setSaving] = useState(false);
  const [feed, setFeed] = useState({ date: "", rows: [] });
  const [mine, setMine] = useState(null);
  const [prevMine, setPrevMine] = useState(null);

  const load = async () => {
    try {
      const [{ data: today_feed }, { data: my_list }] = await Promise.all([
        api.get("/standups/today"),
        api.get("/standups", { params: { user_id: user?.id } }),
      ]);
      setFeed(today_feed);
      const own = (today_feed?.rows || []).find((r) => r.user?.id === user?.id);
      const mineToday = own?.standup || null;
      setMine(mineToday);
      // My most recent PREVIOUS entry — used to prefill "Yesterday".
      const older = (my_list || []).find((s) => !mineToday || s.id !== mineToday.id);
      setPrevMine(older || null);
      if (mineToday) {
        setYesterday(mineToday.yesterday || "");
        setToday(mineToday.today || "");
        setBlockers(mineToday.blockers || "");
      }
    } catch (err) {
      log.debug("[standup] load", err);
    }
  };

  useEffect(() => { if (user?.id) load(); }, [user?.id]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((type) => {
      if (type === "standup.updated") load();
    });
  }, [subscribe, user?.id]);

  const submit = async () => {
    if (!today.trim()) {
      toast.error("Tell the team what you'll work on today");
      return;
    }
    setSaving(true);
    try {
      await api.post("/standups", { yesterday, today, blockers });
      toast.success(mine ? "Stand-up updated" : "Stand-up posted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post stand-up");
    } finally {
      setSaving(false);
    }
  };

  const useYesterdaysToday = () => {
    if (!prevMine?.today) return;
    setYesterday(prevMine.today);
    toast("Filled from your previous 'Today'");
  };

  const posted = (feed.rows || []).filter((r) => r.standup);
  const pending = (feed.rows || []).filter((r) => !r.standup && r.user?.id !== user?.id);
  const dateLabel = feed.date
    ? new Date(feed.date + "T00:00:00").toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })
    : "Today";

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="standup-view">
      <div className="h-14 border-b flex items-center px-6 shrink-0 gap-2">
        <Sunrise className="w-5 h-5 text-[var(--brand-accent)]"/>
        <div>
          <h1 className="text-lg font-semibold leading-tight">Daily stand-up</h1>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{dateLabel}</div>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-zinc-500">
          <span data-testid="standup-team-progress">
            <span className="tabular-nums font-semibold text-zinc-800">{posted.length}</span>
            <span> / {feed.rows?.length || 0} posted</span>
          </span>
          <button
            onClick={load}
            className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"
            title="Refresh"
            data-testid="standup-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5"/>
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden grid grid-cols-1 lg:grid-cols-5">
        {/* Composer */}
        <div className="lg:col-span-2 border-r border-zinc-100 overflow-y-auto scroll-thin p-6 space-y-4">
          <div className="flex items-center gap-2 text-xs">
            {mine ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">
                <CheckCircle2 className="w-3 h-3"/> Posted for today
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                <Clock className="w-3 h-3"/> Not posted yet
              </span>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Yesterday</label>
              {prevMine?.today && !mine && (
                <button
                  onClick={useYesterdaysToday}
                  className="text-[11px] text-[var(--brand-accent)] hover:underline"
                  data-testid="standup-fill-yesterday"
                >
                  Use previous &quot;Today&quot;
                </button>
              )}
            </div>
            <Textarea
              rows={3}
              placeholder="What did you finish yesterday?"
              value={yesterday}
              onChange={(e) => setYesterday(e.target.value)}
              data-testid="standup-yesterday"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Today</label>
            <Textarea
              rows={3}
              placeholder="What will you focus on today?"
              value={today}
              onChange={(e) => setToday(e.target.value)}
              data-testid="standup-today"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-amber-600"/> Blockers <span className="text-zinc-400 normal-case tracking-normal font-normal">(optional)</span>
            </label>
            <Textarea
              rows={2}
              placeholder="Anything slowing you down that the team can help with?"
              value={blockers}
              onChange={(e) => setBlockers(e.target.value)}
              data-testid="standup-blockers"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={submit}
              disabled={saving || !today.trim()}
              className="bg-[var(--brand-accent)] text-white hover:bg-orange-700"
              data-testid="standup-submit"
            >
              {saving ? "Saving…" : (mine ? "Update stand-up" : "Post stand-up")}
            </Button>
          </div>
        </div>

        {/* Team feed */}
        <div className="lg:col-span-3 overflow-y-auto scroll-thin p-6 space-y-4 bg-zinc-50">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
              Team stand-ups · {posted.length}
            </div>
            {posted.length === 0 ? (
              <div className="border border-dashed border-zinc-200 rounded-lg bg-white p-6 text-center text-sm text-zinc-500">
                No one has posted a stand-up yet today. Be the first!
              </div>
            ) : (
              <div className="space-y-3">
                {posted.map((row) => (
                  <StandupCard key={row.user.id} row={row}/>
                ))}
              </div>
            )}
          </div>

          {pending.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2 mt-4">
                Not posted yet · {pending.length}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {pending.map((row) => (
                  <div
                    key={row.user.id}
                    className="flex items-center gap-2 bg-white border border-zinc-200 rounded-lg px-2.5 py-1.5"
                    data-testid={`standup-pending-${row.user.id}`}
                  >
                    <Avatar user={row.user} size="sm"/>
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">{row.user?.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">{row.user?.title || row.membership?.department || row.user?.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StandupCard({ row }) {
  const s = row.standup;
  const posted = new Date(s.updated_at || s.created_at);
  const timeStr = posted.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const hasBlocker = !!(s.blockers && s.blockers.trim());
  return (
    <div
      className={`bg-white border rounded-lg p-4 ${hasBlocker ? "border-amber-300" : "border-zinc-200"}`}
      data-testid={`standup-card-${row.user.id}`}
    >
      <div className="flex items-start gap-3">
        <Avatar user={row.user} size="md"/>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <div className="text-sm font-semibold truncate">{row.user?.name}</div>
            <div className="text-[10px] text-zinc-400 tabular-nums">{timeStr}</div>
            {hasBlocker && (
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded-full">
                <AlertTriangle className="w-3 h-3"/> Blocker
              </span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 truncate">{row.user?.title || row.membership?.department || row.user?.email}</div>
          <div className="mt-3 space-y-2 text-sm">
            {s.yesterday && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Yesterday</div>
                <div className="text-zinc-800 whitespace-pre-wrap break-words">{s.yesterday}</div>
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Today</div>
              <div className="text-zinc-900 whitespace-pre-wrap break-words">{s.today}</div>
            </div>
            {hasBlocker && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-700 font-semibold">Blockers</div>
                <div className="text-zinc-900 whitespace-pre-wrap break-words">{s.blockers}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
